import type { ApolloClient } from '@apollo/client';
import { Apollo } from 'src/gql/generated';

type SkillWithFiles = Pick<
  Apollo.AgentSkillSimpleFragment,
  'attachments' | 'currentVersion' | 'draft' | 'id'
>;

export interface SkillAttachmentContent {
  base64: string;
  contentType: string;
  name: string;
}

const UNAVAILABLE_MESSAGE = 'The attachment is unavailable.';

/**
 * The stored bytes of a skill's file. The author reads the draft's copy by its hash, so a
 * file that was only moved or renamed is not fetched again. Everyone else reads the
 * published version by path.
 */
export function useSkillAttachmentContent({
  path,
  skill,
  skip = false,
}: {
  path: string;
  skill: SkillWithFiles | null;
  skip?: boolean;
}) {
  const sha256 = skill ? getDraftSha256(skill, path) : undefined;
  const isDraft = Boolean(skill?.draft);
  const draftQuery = Apollo.useGetAgentSkillDraftAttachmentQuery({
    skip: skip || !skill || !isDraft || !sha256,
    variables: { sha256: sha256 ?? '', skillId: skill?.id ?? '' },
  });
  const versionQuery = Apollo.useGetAgentSkillAttachmentQuery({
    skip: skip || !skill || isDraft || !skill.currentVersion,
    variables: { path, skillId: skill?.id ?? '', versionId: skill?.currentVersion?.id ?? '' },
  });
  const query = isDraft ? draftQuery : versionQuery;
  return {
    content: isDraft
      ? draftQuery.data?.agentSkillDraftAttachment
      : versionQuery.data?.agentSkillAttachment,
    error: query.error,
    loading: query.loading,
    refetch: query.refetch,
  };
}

/** Which stored copy `path` names: a preview showing the same copy keeps its viewer. */
export function getSkillAttachmentIdentity({
  path,
  skill,
}: {
  path: string;
  skill: SkillWithFiles;
}): string {
  if (skill.draft) return `${skill.id}:draft:${getDraftSha256(skill, path) ?? ''}`;
  return `${skill.id}:${skill.currentVersion?.id ?? ''}:${path}`;
}

/** Fetch a file's bytes outside render, e.g. to download it. */
export async function fetchSkillAttachmentContent({
  client,
  path,
  skill,
}: {
  client: ApolloClient;
  path: string;
  skill: SkillWithFiles;
}): Promise<SkillAttachmentContent> {
  if (skill.draft) {
    const sha256 = getDraftSha256(skill, path);
    if (!sha256) throw new Error(UNAVAILABLE_MESSAGE);
    const result = await client.query<
      Apollo.GetAgentSkillDraftAttachmentQuery,
      Apollo.GetAgentSkillDraftAttachmentQueryVariables
    >({
      errorPolicy: 'none',
      fetchPolicy: 'network-only',
      query: Apollo.GetAgentSkillDraftAttachmentDocument,
      variables: { sha256, skillId: skill.id },
    });
    return requireContent(result.data?.agentSkillDraftAttachment);
  }
  if (!skill.currentVersion) throw new Error(UNAVAILABLE_MESSAGE);
  const result = await client.query<
    Apollo.GetAgentSkillAttachmentQuery,
    Apollo.GetAgentSkillAttachmentQueryVariables
  >({
    errorPolicy: 'none',
    fetchPolicy: 'network-only',
    query: Apollo.GetAgentSkillAttachmentDocument,
    variables: { path, skillId: skill.id, versionId: skill.currentVersion.id },
  });
  return requireContent(result.data?.agentSkillAttachment);
}

/** The bytes of a stored file's base64 content. */
export function decodeSkillAttachmentBytes(base64: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(base64), char => char.charCodeAt(0));
}

function getDraftSha256(skill: SkillWithFiles, path: string): string | undefined {
  return skill.attachments.find(file => file.path === path)?.sha256;
}

function requireContent(
  content: SkillAttachmentContent | null | undefined
): SkillAttachmentContent {
  if (!content) throw new Error(UNAVAILABLE_MESSAGE);
  return content;
}
